// client/src/pages/DecoratorsPage.jsx
//
// Decorators — decoration pricing matrices + the vendors (decorators) they
// belong to. This is step 1 of decorated-apparel quoting: a self-contained
// place to define per-vendor pricing tables. It has no supplier dependency —
// matrices work whether or not SanMar/S&S are connected.
//
// A "matrix" is a pricing table:
//   • Screen print   — rows are # colors, columns are qty tiers, cells are
//     per-piece run cost. A blank cell = that color/qty combo is unavailable.
//   • Embroidery     — rows are stitch-count bands, columns are qty tiers.
//   • Heat Transfer  — rows are print-size bands (sq in, from width × height
//     entered on the job), columns are qty tiers. Same {min,max,prices[]}
//     row shape as embroidery's stitch bands — just a different unit.
//   • Other          — one per-piece price per qty tier (vinyl, other one-offs…).
// Plus an à la carte charge library (setup, screen fees, digitizing, etc.) that
// the quoting flow will let you add per job — nothing here is auto-applied.
//
// Lookup, as the distributor described it: pick # colors (or stitch/size
// band), the total garment qty lands in a tier, read the cell. The preview
// panel in the editor does exactly that so you can sanity-check a matrix as
// you build it.
//
// Persisted shape (decoration_matrices.pricing JSONB):
//   { qty_tiers:[{min,max}], max_colors, rows:[...], charges:[{name,amount,basis}] }
// where a row is {colors, prices:[]} (screen), {min,max,prices:[]}
// (embroidery AND heat_transfer — same shape, min/max is sq in instead of
// stitches for heat_transfer), or {prices:[]} (other); prices align to
// qty_tiers, null = unavailable.

import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

const TYPES = [
  { id: 'screen_print',  label: 'Screen Print' },
  { id: 'embroidery',    label: 'Embroidery'   },
  { id: 'heat_transfer', label: 'Heat Transfer' },
  { id: 'other',         label: 'Other'        },
];
const typeLabel = (t) => (TYPES.find(x => x.id === t) || {}).label || t;

const BASES = [
  { id: 'flat',         label: 'Flat (per job)' },
  { id: 'per_color',    label: 'Per color' },
  { id: 'per_screen',   label: 'Per screen' },
  { id: 'per_location', label: 'Per location' },
  { id: 'per_unit',     label: 'Per unit' },
];

const inputStyle = {
  width:'100%',border:'1px solid #E2E8F0',borderRadius:7,padding:'8px 11px',
  fontSize:12,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box',
};
const cellStyle = {
  width:'100%',border:'1px solid #E2E8F0',borderRadius:6,padding:'5px 6px',
  fontSize:12,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',
  boxSizing:'border-box',textAlign:'right',
};

// ── Default tiers match the distributor's own screen-print matrix screenshot ──
const DEFAULT_TIERS = [
  { min: 12,   max: 24   },
  { min: 25,   max: 48   },
  { min: 49,   max: 72   },
  { min: 73,   max: 144  },
  { min: 145,  max: 250  },
  { min: 251,  max: 600  },
  { min: 601,  max: 1000 },
  { min: 1001, max: null },
];

const tierLabel = (t) =>
  (t.max == null || t.max === '') ? `${t.min}+` : `${t.min}–${t.max}`;

// Resolve which tier index a quantity falls into (highest break whose min <= qty).
function resolveTierIndex(tiers, qty) {
  if (!Array.isArray(tiers) || tiers.length === 0 || !(qty > 0)) return -1;
  let idx = -1;
  for (let i = 0; i < tiers.length; i++) {
    const min = Number(tiers[i].min);
    const max = tiers[i].max == null || tiers[i].max === '' ? Infinity : Number(tiers[i].max);
    if (qty >= min && qty <= max) return i;
    if (qty >= min) idx = i; // fallback: last tier whose min we've cleared
  }
  return idx;
}

// ── Excel import/export helpers: tier labels, cell coercion, grid parsing ─────
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// {min,max} → "12-24" or "1001+" (open top tier).
function formatTierLabel(t) {
  if (t.max == null || t.max === '') return `${t.min}+`;
  return `${t.min}-${t.max}`;
}

// "12-24" / "1001+" / "1001-2999" / "1001" → {min,max}. Tolerates en/em dashes,
// commas, and spaces so a hand-edited sheet still parses.
function parseTierLabel(raw) {
  const s = String(raw == null ? '' : raw).replace(/[\u2013\u2014]/g, '-').replace(/[,\s]/g, '').trim();
  if (!s) return null;
  if (s.endsWith('+')) { const n = parseInt(s, 10); return Number.isFinite(n) ? { min: n, max: null } : null; }
  if (s.includes('-')) {
    const [a, b] = s.split('-');
    const min = parseInt(a, 10);
    return Number.isFinite(min) ? { min, max: (b === '' || b == null) ? null : parseInt(b, 10) } : null;
  }
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? { min: n, max: null } : null;
}

// Spreadsheet cell → price number, or null for "unavailable" (X, -, blank, n/a).
function parseCell(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? round2(v) : null;
  const t = String(v).trim();
  if (!t || /^(x|-|\u2014|n\/?a)$/i.test(t)) return null;
  const n = Number(t.replace(/[$,]/g, ''));
  return Number.isFinite(n) ? round2(n) : null;
}

// Array-of-arrays sheet → pricing grid for a matrix type. Row 0 is the header
// [firstColLabel, ...tierLabels]; each data row is keyed by its first column.
function parseGrid(aoa, type) {
  if (!Array.isArray(aoa) || aoa.length === 0) return null;
  let h = 0;
  while (h < aoa.length && (!aoa[h] || aoa[h].filter(c => c !== '' && c != null).length < 2)) h++;
  if (h >= aoa.length) return null;
  const header = aoa[h];
  const qty_tiers = header.slice(1)
    .filter(c => c !== '' && c != null)
    .map(parseTierLabel)
    .filter(t => t && Number.isFinite(t.min));
  if (!qty_tiers.length) return null;
  const width = qty_tiers.length;
  const fix = (arr) => {
    const a = (arr || []).slice(0, width).map(parseCell);
    while (a.length < width) a.push(null);
    return a;
  };
  const dataRows = aoa.slice(h + 1).filter(r => r && r.length && r[0] !== '' && r[0] != null);
  if (type === 'screen_print') {
    const rows = dataRows
      .map(r => ({ colors: parseInt(String(r[0]).replace(/[^0-9]/g, ''), 10), prices: fix(r.slice(1)) }))
      .filter(r => Number.isFinite(r.colors) && r.colors > 0)
      .sort((a, b) => a.colors - b.colors);
    if (!rows.length) return null;
    return { qty_tiers, max_colors: rows[rows.length - 1].colors, rows, charges: [] };
  }
  if (type === 'embroidery' || type === 'heat_transfer') {
    const rows = dataRows.map(r => {
      const b = parseTierLabel(r[0]) || { min: 0, max: null };
      return { min: b.min || 0, max: b.max, prices: fix(r.slice(1)) };
    });
    if (!rows.length) return null;
    return { qty_tiers, rows, charges: [] };
  }
  const first = dataRows[0] || [];
  return { qty_tiers, rows: [{ prices: fix(first.slice(1)) }], charges: [] };
}

// Illustrative prices for the template's "Example filled in" sheet: descends
// with quantity, rises with the row index — just to show the shape.
function exampleRowsFor(type, tierLabels, rowLabels) {
  const mk = (ri) => tierLabels.map((_, ti) => Math.max(0.5, 2.5 + ri * 0.1 - ti * 0.15).toFixed(2));
  if (type === 'other') return [['Per pc', ...mk(0)]];
  return rowLabels.map((label, ri) => [label, ...mk(ri)]);
}

function emptyPricing(type) {
  const tiers = DEFAULT_TIERS.map(t => ({ ...t }));
  const width = tiers.length;
  const blankRow = () => Array(width).fill(null);
  if (type === 'screen_print') {
    const maxColors = 6;
    const rows = [];
    for (let c = 1; c <= maxColors; c++) rows.push({ colors: c, prices: blankRow() });
    return { qty_tiers: tiers, max_colors: maxColors, rows, charges: [] };
  }
  if (type === 'embroidery') {
    return {
      qty_tiers: tiers,
      rows: [
        { min: 0,     max: 5000,  prices: blankRow() },
        { min: 5001,  max: 10000, prices: blankRow() },
        { min: 10001, max: 15000, prices: blankRow() },
      ],
      charges: [],
    };
  }
  if (type === 'heat_transfer') {
    return {
      qty_tiers: tiers,
      rows: [
        { min: 0,  max: 20,   prices: blankRow() },
        { min: 20, max: 64,   prices: blankRow() },
        { min: 64, max: null, prices: blankRow() },
      ],
      charges: [],
    };
  }
  return { qty_tiers: tiers, rows: [{ prices: blankRow() }], charges: [] };
}

// Normalize a loaded matrix's pricing so the editor always has aligned arrays.
function normalizePricing(type, pricing) {
  const p = pricing && typeof pricing === 'object' ? pricing : {};
  const base = emptyPricing(type);
  const tiers = Array.isArray(p.qty_tiers) && p.qty_tiers.length ? p.qty_tiers.map(t => ({ ...t })) : base.qty_tiers;
  const width = tiers.length;
  const fix = (prices) => {
    const arr = Array.isArray(prices) ? prices.slice(0, width) : [];
    while (arr.length < width) arr.push(null);
    return arr;
  };
  let rows;
  if (type === 'screen_print') {
    const maxColors = Number(p.max_colors) || (Array.isArray(p.rows) ? p.rows.length : 6) || 6;
    rows = [];
    for (let c = 1; c <= maxColors; c++) {
      const existing = (p.rows || []).find(r => Number(r.colors) === c);
      rows.push({ colors: c, prices: fix(existing?.prices) });
    }
    return { qty_tiers: tiers, max_colors: maxColors, rows, charges: Array.isArray(p.charges) ? p.charges : [] };
  }
  if (type === 'embroidery' || type === 'heat_transfer') {
    rows = (Array.isArray(p.rows) && p.rows.length ? p.rows : base.rows)
      .map(r => ({ min: r.min ?? 0, max: r.max ?? null, prices: fix(r.prices) }));
    return { qty_tiers: tiers, rows, charges: Array.isArray(p.charges) ? p.charges : [] };
  }
  rows = [{ prices: fix((p.rows && p.rows[0] && p.rows[0].prices) || []) }];
  return { qty_tiers: tiers, rows, charges: Array.isArray(p.charges) ? p.charges : [] };
}

export default function DecoratorsPage({ getToken, showToast }) {
  const [tab, setTab] = useState('matrices'); // 'matrices' | 'vendors'
  const [matrices, setMatrices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // matrix object or {__new:true}
  const [editVendor, setEditVendor] = useState(null); // vendor object or {__new:true}
  const [filterType, setFilterType]     = useState('all'); // 'all' | a matrix type
  const [filterVendor, setFilterVendor] = useState('all'); // 'all' | a vendor id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const t = api.withToken(token);
      const [m, v] = await Promise.all([t.listDecorationMatrices(), t.listVendors()]);
      setMatrices(m); setVendors(v);
    } catch (e) {
      showToast?.('Failed to load decorators: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, showToast]);

  useEffect(() => { load(); }, [load]);

  const activeVendors = vendors.filter(v => !v.archived_at);
  const filteredMatrices = matrices.filter(m =>
    (filterType === 'all'   || m.type === filterType) &&
    (filterVendor === 'all' || m.vendor_id === filterVendor)
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
        <div>
          <div style={{ fontSize:22,fontWeight:700,color:'#0F172A',letterSpacing:'-0.3px' }}>Decorators</div>
          <div style={{ fontSize:13,color:'#64748B',marginTop:4 }}>
            Decoration pricing matrices and the decorators they belong to. Used when quoting decorated apparel.
          </div>
        </div>
        {tab === 'matrices' ? (
          <button onClick={() => setEditing({ __new:true })}
            style={{ background:G,border:'none',borderRadius:8,padding:'9px 18px',
              color:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
            + New matrix
          </button>
        ) : (
          <button onClick={() => setEditVendor({ __new:true })}
            style={{ background:G,border:'none',borderRadius:8,padding:'9px 18px',
              color:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
            + New decorator
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex',gap:4,marginBottom:18,borderBottom:'1px solid #E2E8F0' }}>
        {[['matrices','Pricing Matrices'],['vendors','Decorators']].map(([id,label]) => (
          <div key={id} onClick={() => setTab(id)}
            style={{ padding:'8px 14px',fontSize:13,cursor:'pointer',fontWeight:tab===id?600:500,
              color: tab===id ? G : '#64748B',
              borderBottom: tab===id ? `2px solid ${G}` : '2px solid transparent',marginBottom:-1 }}>
            {label}
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ padding:40,textAlign:'center',color:'#94A3B8',fontSize:13 }}>Loading…</div>
      ) : tab === 'matrices' ? (
        <>
          {matrices.length > 0 && (
            <div style={{ display:'flex',gap:10,marginBottom:14,flexWrap:'wrap' }}>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                style={{ ...inputStyle, width:'auto', minWidth:150 }}>
                <option value="all">All types</option>
                {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <select value={filterVendor} onChange={e => setFilterVendor(e.target.value)}
                style={{ ...inputStyle, width:'auto', minWidth:170 }}>
                <option value="all">All decorators</option>
                {activeVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              {(filterType !== 'all' || filterVendor !== 'all') && (
                <button onClick={() => { setFilterType('all'); setFilterVendor('all'); }}
                  style={{ background:'none',border:'none',color:G,fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
                  Clear filters
                </button>
              )}
            </div>
          )}
          <MatrixList
            matrices={filteredMatrices}
            onOpen={(m) => setEditing(m)}
            onNew={() => setEditing({ __new:true })}
            filtered={matrices.length > 0 && filteredMatrices.length === 0}
          />
        </>
      ) : (
        <VendorList
          vendors={activeVendors}
          onOpen={(v) => setEditVendor(v)}
          onNew={() => setEditVendor({ __new:true })}
        />
      )}

      {editing && (
        <MatrixEditor
          matrix={editing.__new ? null : editing}
          vendors={activeVendors}
          getToken={getToken}
          showToast={showToast}
          onAddVendor={() => setEditVendor({ __new:true, __returnToMatrix:true })}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onArchived={() => { setEditing(null); load(); }}
        />
      )}

      {editVendor && (
        <VendorModal
          vendor={editVendor.__new ? null : editVendor}
          getToken={getToken}
          showToast={showToast}
          onClose={() => setEditVendor(null)}
          onSaved={async (saved) => {
            const returnToMatrix = editVendor.__returnToMatrix;
            setEditVendor(null);
            await load();
            // If we came here from the matrix editor's "+ Add decorator", pop back.
            if (returnToMatrix && editing) setEditing(e => e); // keep matrix editor open
          }}
        />
      )}
    </div>
  );
}

// ─── Matrix list (grouped by type) ───────────────────────────────────────────
function MatrixList({ matrices, onOpen, onNew, filtered }) {
  if (matrices.length === 0) {
    return (
      <div style={{ background:'#fff',border:'1px dashed #E2E8F0',borderRadius:12,
        padding:'48px 24px',textAlign:'center' }}>
        <div style={{ fontSize:14,fontWeight:600,color:'#0F172A',marginBottom:6 }}>
          {filtered ? 'No matrices match these filters.' : 'No pricing matrices yet.'}
        </div>
        <div style={{ fontSize:12,color:'#64748B',marginBottom:14 }}>
          {filtered
            ? 'Try a different type or decorator.'
            : 'Build a screen-print, embroidery, or other pricing table for one of your decorators.'}
        </div>
        {!filtered && (
          <button onClick={onNew}
            style={{ background:G,border:'none',borderRadius:7,padding:'8px 16px',
              color:'#fff',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
            + New matrix
          </button>
        )}
      </div>
    );
  }

  // Group by type, canonical order first (screen → embroidery → other), then any
  // unknown types after.
  const order = TYPES.map(t => t.id);
  const groups = {};
  for (const m of matrices) (groups[m.type] = groups[m.type] || []).push(m);
  const typeIds = [
    ...order.filter(t => groups[t]),
    ...Object.keys(groups).filter(t => !order.includes(t)),
  ];

  return (
    <div>
      {typeIds.map(tid => (
        <div key={tid} style={{ marginBottom:22 }}>
          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10 }}>
            <span style={{ fontSize:13,fontWeight:700,color:'#0F172A' }}>{typeLabel(tid)}</span>
            <span style={{ fontSize:11,color:'#64748B',background:'#F1F5F9',borderRadius:10,padding:'1px 8px',fontWeight:600 }}>
              {groups[tid].length}
            </span>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))',gap:12 }}>
            {groups[tid].map(m => {
              const tiers = (m.pricing && m.pricing.qty_tiers) || [];
              const rows = (m.pricing && m.pricing.rows) || [];
              const charges = (m.pricing && m.pricing.charges) || [];
              return (
                <div key={m.id} onClick={() => onOpen(m)}
                  style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:10,padding:16,cursor:'pointer' }}>
                  <div style={{ fontWeight:600,color:'#0F172A',fontSize:14,marginBottom:6 }}>{m.name}</div>
                  <div style={{ fontSize:12,color:'#475569',marginBottom:8 }}>
                    {m.vendor_name
                      ? <>Decorator: <strong style={{ color:'#0F172A' }}>{m.vendor_name}</strong>{m.vendor_archived_at ? ' (archived)' : ''}</>
                      : <span style={{ color:'#B91C1C' }}>No decorator assigned</span>}
                  </div>
                  <div style={{ fontSize:11,color:'#94A3B8' }}>
                    {rows.length} row{rows.length===1?'':'s'} · {tiers.length} qty tier{tiers.length===1?'':'s'}
                    {charges.length ? ` · ${charges.length} à la carte fee${charges.length===1?'':'s'}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Vendor list ─────────────────────────────────────────────────────────────
function VendorList({ vendors, onOpen, onNew }) {
  if (vendors.length === 0) {
    return (
      <div style={{ background:'#fff',border:'1px dashed #E2E8F0',borderRadius:12,
        padding:'48px 24px',textAlign:'center' }}>
        <div style={{ fontSize:14,fontWeight:600,color:'#0F172A',marginBottom:6 }}>No decorators yet.</div>
        <div style={{ fontSize:12,color:'#64748B',marginBottom:14 }}>
          Add the shops you send decoration work to. You'll assign a matrix to each.
        </div>
        <button onClick={onNew}
          style={{ background:G,border:'none',borderRadius:7,padding:'8px 16px',
            color:'#fff',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
          + New decorator
        </button>
      </div>
    );
  }
  return (
    <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:10,overflow:'hidden' }}>
      <div style={{ display:'grid',gridTemplateColumns:'2fr 2fr 1.5fr',gap:16,
        padding:'10px 16px',background:'#F8FAFC',fontSize:11,fontWeight:700,textTransform:'uppercase',
        color:'#64748B',borderBottom:'1px solid #E2E8F0',letterSpacing:'0.4px' }}>
        <div>Name</div><div>Email</div><div>Phone</div>
      </div>
      {vendors.map(v => (
        <div key={v.id} onClick={() => onOpen(v)}
          style={{ display:'grid',gridTemplateColumns:'2fr 2fr 1.5fr',gap:16,
            padding:'14px 16px',fontSize:13,borderBottom:'1px solid #F1F5F9',cursor:'pointer' }}>
          <div style={{ fontWeight:500,color:'#0F172A' }}>{v.name}</div>
          <div style={{ color:'#475569' }}>{v.email || '—'}</div>
          <div style={{ color:'#475569' }}>{v.phone || '—'}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Matrix editor ───────────────────────────────────────────────────────────
function MatrixEditor({ matrix, vendors, getToken, showToast, onClose, onSaved, onArchived, onAddVendor }) {
  const isNew = !matrix;
  const [name, setName]       = useState(matrix?.name || '');
  const [type, setType]       = useState(matrix?.type || 'screen_print');
  const [vendorId, setVendorId] = useState(matrix?.vendor_id || '');
  const [pricing, setPricing] = useState(() => normalizePricing(matrix?.type || 'screen_print', matrix?.pricing));
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState(null);

  // If the parent added a vendor, it re-renders `vendors`; auto-select the newest
  // when we had none selected.
  useEffect(() => {
    if (!vendorId && vendors.length === 1) setVendorId(vendors[0].id);
  }, [vendors, vendorId]);

  const changeType = (t) => {
    setType(t);
    // Re-shape pricing to the new type but keep tiers + charges.
    setPricing(prev => {
      const fresh = emptyPricing(t);
      return normalizePricing(t, { qty_tiers: prev.qty_tiers, charges: prev.charges, rows: fresh.rows, max_colors: fresh.max_colors });
    });
  };

  // ── Tier ops ──
  const addTier = () => setPricing(p => {
    const last = p.qty_tiers[p.qty_tiers.length - 1];
    const newMin = last ? (Number(last.max) || Number(last.min) || 0) + 1 : 0;
    return {
      ...p,
      qty_tiers: [...p.qty_tiers, { min: newMin, max: null }],
      rows: p.rows.map(r => ({ ...r, prices: [...r.prices, null] })),
    };
  });
  const removeTier = (i) => setPricing(p => ({
    ...p,
    qty_tiers: p.qty_tiers.filter((_, k) => k !== i),
    rows: p.rows.map(r => ({ ...r, prices: r.prices.filter((_, k) => k !== i) })),
  }));
  const setTierField = (i, field, val) => setPricing(p => ({
    ...p,
    qty_tiers: p.qty_tiers.map((t, k) => k === i ? { ...t, [field]: val === '' ? (field==='max'?null:'') : Number(val) } : t),
  }));

  // ── Cell ops ──
  const setCell = (rowIdx, tierIdx, val) => setPricing(p => ({
    ...p,
    rows: p.rows.map((r, k) => k === rowIdx
      ? { ...r, prices: r.prices.map((v, j) => j === tierIdx ? val : v) }
      : r),
  }));

  // ── Screen: color rows ──
  const setMaxColors = (n) => setPricing(p => {
    const target = Math.max(1, Math.min(12, Number(n) || 1));
    const width = p.qty_tiers.length;
    const rows = [];
    for (let c = 1; c <= target; c++) {
      const existing = p.rows.find(r => Number(r.colors) === c);
      rows.push(existing || { colors: c, prices: Array(width).fill(null) });
    }
    return { ...p, max_colors: target, rows };
  });

  // ── Embroidery: band rows ──
  const addBand = () => setPricing(p => {
    const last = p.rows[p.rows.length - 1];
    const newMin = last ? (Number(last.max) || 0) + 1 : 0;
    return { ...p, rows: [...p.rows, { min: newMin, max: null, prices: Array(p.qty_tiers.length).fill(null) }] };
  });
  const removeBand = (i) => setPricing(p => ({ ...p, rows: p.rows.filter((_, k) => k !== i) }));
  const setBandField = (i, field, val) => setPricing(p => ({
    ...p,
    rows: p.rows.map((r, k) => k === i ? { ...r, [field]: val === '' ? (field==='max'?null:'') : Number(val) } : r),
  }));

  // ── Charges ──
  const addCharge = (preset) => setPricing(p => ({
    ...p, charges: [...(p.charges||[]), preset || { name:'', amount:0, basis:'flat' }],
  }));
  const setCharge = (i, field, val) => setPricing(p => ({
    ...p,
    charges: p.charges.map((c, k) => k === i ? { ...c, [field]: val } : c),
  }));
  const removeCharge = (i) => setPricing(p => ({ ...p, charges: p.charges.filter((_, k) => k !== i) }));

  // ── Excel template download + import (SheetJS, lazy-loaded) ──
  const downloadTemplate = async () => {
    setErr(null);
    try {
      const XLSX = await import('xlsx');
      const tierLabels = pricing.qty_tiers.map(formatTierLabel);
      let firstCol, tmplRows, exRowLabels;
      if (type === 'screen_print') {
        firstCol = 'Colors';
        tmplRows = [];
        for (let c = 1; c <= pricing.max_colors; c++) tmplRows.push([c, ...tierLabels.map(() => '')]);
        exRowLabels = Array.from({ length: pricing.max_colors }, (_, i) => i + 1);
      } else if (type === 'embroidery' || type === 'heat_transfer') {
        firstCol = type === 'heat_transfer' ? 'Sq in' : 'Stitches';
        tmplRows = pricing.rows.map(r => [formatTierLabel({ min: r.min, max: r.max }), ...tierLabels.map(() => '')]);
        exRowLabels = pricing.rows.map(r => formatTierLabel({ min: r.min, max: r.max }));
      } else {
        firstCol = 'Price';
        tmplRows = [['Per pc', ...tierLabels.map(() => '')]];
        exRowLabels = ['Per pc'];
      }
      const templateAoa = [[firstCol, ...tierLabels], ...tmplRows];
      const exampleAoa  = [[firstCol, ...tierLabels], ...exampleRowsFor(type, tierLabels, exRowLabels)];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(templateAoa), 'Template');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(exampleAoa), 'Example filled in');
      XLSX.writeFile(wb, `pricing_template_${type}.xlsx`);
    } catch (e) {
      setErr('Could not build template: ' + (e.message || e));
    }
  };

  const importFromExcel = async (file) => {
    setErr(null);
    try {
      const XLSX = await import('xlsx');
      const data = new Uint8Array(await file.arrayBuffer());
      const wb = XLSX.read(data, { type: 'array' });
      let parsed = null, fallback = null;
      for (const sheetName of wb.SheetNames) {
        const aoa = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, blankrows: false });
        const p = parseGrid(aoa, type);
        if (!p) continue;
        if (p.rows.some(r => r.prices.some(x => x != null))) { parsed = p; break; } // prefer a filled sheet
        if (!fallback) fallback = p;
      }
      const result = parsed || fallback;
      if (!result) { setErr('No pricing table found in that file — use the downloaded template.'); return; }
      setPricing(prev => ({ ...result, charges: prev.charges })); // keep à la carte fees
      showToast?.(`Imported ${result.rows.length} row(s) × ${result.qty_tiers.length} tier(s)`, 'success');
    } catch (e) {
      setErr('Import failed: ' + (e.message || e));
    }
  };

  const save = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    if (!vendorId)    { setErr('Assign a decorator (vendor)'); return; }
    setSaving(true); setErr(null);
    // Clean charges: drop nameless rows, coerce amounts.
    const cleanPricing = {
      ...pricing,
      charges: (pricing.charges || [])
        .filter(c => (c.name || '').trim())
        .map(c => ({ name: c.name.trim(), amount: Number(c.amount) || 0, basis: c.basis || 'flat' })),
    };
    try {
      const token = await getToken();
      const t = api.withToken(token);
      const body = { name: name.trim(), type, vendor_id: vendorId, pricing: cleanPricing };
      if (isNew) await t.createDecorationMatrix(body);
      else       await t.updateDecorationMatrix(matrix.id, body);
      showToast?.(isNew ? 'Matrix created' : 'Matrix saved', 'success');
      onSaved();
    } catch (e) {
      setErr(e.message || 'Could not save');
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm('Archive this matrix? It will no longer be available for quoting.')) return;
    setSaving(true);
    try {
      const token = await getToken();
      await api.withToken(token).archiveDecorationMatrix(matrix.id);
      showToast?.('Matrix archived', 'success');
      onArchived();
    } catch (e) {
      setErr(e.message || 'Could not archive');
      setSaving(false);
    }
  };

  const tiers = pricing.qty_tiers;

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',zIndex:200,
      display:'flex',alignItems:'flex-start',justifyContent:'center',backdropFilter:'blur(4px)',
      padding:'3vh 20px',overflowY:'auto' }}
      onClick={e => e.target === e.currentTarget && !saving && onClose()}>
      <div style={{ background:'#fff',borderRadius:14,padding:26,maxWidth:920,width:'100%',
        boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>

        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18 }}>
          <div style={{ fontSize:18,fontWeight:600,color:'#0F172A' }}>{isNew ? 'New pricing matrix' : 'Edit matrix'}</div>
          <button onClick={() => !saving && onClose()}
            style={{ background:'none',border:'none',color:'#94A3B8',fontSize:22,cursor:'pointer',padding:0,lineHeight:1 }}>×</button>
        </div>

        {/* Top fields */}
        <div style={{ display:'grid',gridTemplateColumns:'2fr 1.4fr 1.2fr',gap:12,marginBottom:18 }}>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Matrix name *</label>
            <input value={name} onChange={e => setName(e.target.value)} autoFocus
              placeholder="e.g. Standard Screen Print" style={inputStyle}/>
          </div>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Decorator *</label>
            <select value={vendorId}
              onChange={e => { if (e.target.value === '__add') { onAddVendor(); } else setVendorId(e.target.value); }}
              style={inputStyle}>
              <option value="">Select decorator…</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              <option value="__add">+ Add decorator…</option>
            </select>
          </div>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Type</label>
            <select value={type} onChange={e => changeType(e.target.value)} style={inputStyle}>
              {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
        </div>

        {/* Quantity tiers */}
        <SectionLabel>Quantity tiers</SectionLabel>
        <div style={{ fontSize:11,color:'#94A3B8',marginBottom:8 }}>
          Total garment quantity picks the tier. Leave the last tier's "to" blank for an open-ended top tier (e.g. 1001+).
        </div>
        <div style={{ display:'flex',flexWrap:'wrap',gap:8,marginBottom:18 }}>
          {tiers.map((t, i) => (
            <div key={i} style={{ display:'flex',alignItems:'center',gap:4,border:'1px solid #E2E8F0',
              borderRadius:8,padding:'4px 6px 4px 8px',background:'#F8FAFC' }}>
              <input value={t.min} onChange={e => setTierField(i,'min',e.target.value)}
                style={{ ...cellStyle,width:52,textAlign:'center' }} placeholder="min"/>
              <span style={{ color:'#94A3B8',fontSize:12 }}>–</span>
              <input value={t.max == null ? '' : t.max} onChange={e => setTierField(i,'max',e.target.value)}
                style={{ ...cellStyle,width:52,textAlign:'center' }} placeholder="∞"/>
              <button onClick={() => removeTier(i)} title="Remove tier"
                style={{ background:'none',border:'none',color:'#CBD5E1',fontSize:16,cursor:'pointer',padding:'0 2px',lineHeight:1 }}>×</button>
            </div>
          ))}
          <button onClick={addTier}
            style={{ border:'1px dashed #CBD5E1',background:'none',borderRadius:8,padding:'6px 12px',
              fontSize:12,color:G,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>+ Tier</button>
        </div>

        {/* Price grid */}
        <SectionLabel>
          {type === 'screen_print' ? 'Per-piece price by colors × quantity'
            : type === 'embroidery' ? 'Per-piece price by stitch count × quantity'
            : type === 'heat_transfer' ? 'Price PER SQUARE INCH by size band × quantity (multiplied by actual sq in at quote time)'
            : 'Per-piece price by quantity'}
        </SectionLabel>
        <div style={{ fontSize:11,color:'#94A3B8',marginBottom:8 }}>
          Leave a cell blank if that combination isn't offered (shows as unavailable).
        </div>

        <div style={{ display:'flex',gap:8,alignItems:'center',marginBottom:10,flexWrap:'wrap' }}>
          <button onClick={downloadTemplate} style={ghostBtn}>⬇ Excel template</button>
          <label style={{ ...ghostBtn, display:'inline-flex', alignItems:'center' }}>
            ⬆ Import from Excel
            <input type="file" accept=".xlsx,.xls" style={{ display:'none' }}
              onChange={e => { const f = e.target.files && e.target.files[0]; if (f) importFromExcel(f); e.target.value = ''; }}/>
          </label>
          <span style={{ fontSize:11,color:'#94A3B8' }}>
            Download the template, fill it in, upload — it fills the grid below. Import replaces the grid; fees are kept.
          </span>
        </div>

        <div style={{ overflowX:'auto',border:'1px solid #E2E8F0',borderRadius:10,marginBottom:8 }}>
          <table style={{ borderCollapse:'collapse',width:'100%',minWidth:520 }}>
            <thead>
              <tr style={{ background:'#F8FAFC' }}>
                <th style={thStyle}>
                  {type === 'screen_print' ? 'Colors' : type === 'embroidery' ? 'Stitches' : type === 'heat_transfer' ? 'Sq in' : 'Price'}
                </th>
                {tiers.map((t, i) => (
                  <th key={i} style={{ ...thStyle,textAlign:'right' }}>{tierLabel(t)}</th>
                ))}
                {(type === 'embroidery' || type === 'heat_transfer') && <th style={thStyle}></th>}
              </tr>
            </thead>
            <tbody>
              {type === 'screen_print' && pricing.rows.map((r, ri) => (
                <tr key={ri} style={{ borderTop:'1px solid #F1F5F9' }}>
                  <td style={{ ...tdStyle,fontWeight:600,color:'#0F172A' }}>{r.colors}</td>
                  {r.prices.map((v, ti) => (
                    <td key={ti} style={tdStyle}>
                      <PriceCell value={v} onChange={val => setCell(ri, ti, val)} />
                    </td>
                  ))}
                </tr>
              ))}

              {(type === 'embroidery' || type === 'heat_transfer') && pricing.rows.map((r, ri) => (
                <tr key={ri} style={{ borderTop:'1px solid #F1F5F9' }}>
                  <td style={{ ...tdStyle,minWidth:150 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:4 }}>
                      <input value={r.min} onChange={e => setBandField(ri,'min',e.target.value)}
                        style={{ ...cellStyle,width:56,textAlign:'center' }} placeholder="min"/>
                      <span style={{ color:'#94A3B8',fontSize:12 }}>–</span>
                      <input value={r.max == null ? '' : r.max} onChange={e => setBandField(ri,'max',e.target.value)}
                        style={{ ...cellStyle,width:56,textAlign:'center' }} placeholder="∞"/>
                    </div>
                  </td>
                  {r.prices.map((v, ti) => (
                    <td key={ti} style={tdStyle}>
                      <PriceCell value={v} onChange={val => setCell(ri, ti, val)} />
                    </td>
                  ))}
                  <td style={tdStyle}>
                    <button onClick={() => removeBand(ri)} title="Remove band"
                      style={{ background:'none',border:'none',color:'#CBD5E1',fontSize:16,cursor:'pointer',padding:0,lineHeight:1 }}>×</button>
                  </td>
                </tr>
              ))}

              {type === 'other' && (
                <tr style={{ borderTop:'1px solid #F1F5F9' }}>
                  <td style={{ ...tdStyle,fontWeight:600,color:'#0F172A' }}>Per pc</td>
                  {pricing.rows[0].prices.map((v, ti) => (
                    <td key={ti} style={tdStyle}>
                      <PriceCell value={v} onChange={val => setCell(0, ti, val)} />
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Row controls */}
        <div style={{ marginBottom:20 }}>
          {type === 'screen_print' && (
            <div style={{ display:'flex',alignItems:'center',gap:8,fontSize:12,color:'#475569' }}>
              Max colors:
              <input type="number" min={1} max={12} value={pricing.max_colors}
                onChange={e => setMaxColors(e.target.value)}
                style={{ ...cellStyle,width:56,textAlign:'center' }}/>
            </div>
          )}
          {type === 'embroidery' && (
            <button onClick={addBand}
              style={{ border:'1px dashed #CBD5E1',background:'none',borderRadius:8,padding:'6px 12px',
                fontSize:12,color:G,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>+ Stitch band</button>
          )}
          {type === 'heat_transfer' && (
            <button onClick={addBand}
              style={{ border:'1px dashed #CBD5E1',background:'none',borderRadius:8,padding:'6px 12px',
                fontSize:12,color:G,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>+ Size band</button>
          )}
        </div>

        {/* À la carte charges */}
        <SectionLabel>À la carte fees (optional)</SectionLabel>
        <div style={{ fontSize:11,color:'#94A3B8',marginBottom:8 }}>
          Setup, screen fees, reset fees, digitizing, etc. None are auto-applied — you pick which apply per job when quoting.
        </div>
        <div style={{ marginBottom:8 }}>
          {(pricing.charges || []).map((c, i) => (
            <div key={i} style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1.4fr 28px',gap:8,marginBottom:6,alignItems:'center' }}>
              <input value={c.name} onChange={e => setCharge(i,'name',e.target.value)}
                placeholder="Fee name (e.g. Screen fee)" style={inputStyle}/>
              <div style={{ position:'relative' }}>
                <span style={{ position:'absolute',left:10,top:8,fontSize:12,color:'#94A3B8',zIndex:1 }}>$</span>
                <NumInput value={c.amount === '' || c.amount == null ? null : c.amount}
                  onChange={v => setCharge(i,'amount', v)} placeholder="0.00"
                  style={{ ...inputStyle, paddingLeft:20 }}/>
              </div>
              <select value={c.basis} onChange={e => setCharge(i,'basis',e.target.value)} style={inputStyle}>
                {BASES.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
              <button onClick={() => removeCharge(i)}
                style={{ background:'none',border:'none',color:'#CBD5E1',fontSize:18,cursor:'pointer',padding:0,lineHeight:1 }}>×</button>
            </div>
          ))}
        </div>
        <div style={{ display:'flex',gap:8,marginBottom:20,flexWrap:'wrap' }}>
          <button onClick={() => addCharge()} style={ghostBtn}>+ Fee</button>
          <button onClick={() => addCharge({ name:'Setup', amount:0, basis:'flat' })} style={ghostBtn}>+ Setup (flat)</button>
          <button onClick={() => addCharge({ name:'Screen fee', amount:0, basis:'per_color' })} style={ghostBtn}>+ Screen fee (per color)</button>
          {type === 'embroidery' &&
            <button onClick={() => addCharge({ name:'Digitizing', amount:0, basis:'flat' })} style={ghostBtn}>+ Digitizing (flat)</button>}
        </div>

        {/* Live lookup preview */}
        <LookupPreview type={type} pricing={pricing} />

        {err && (
          <div style={{ margin:'14px 0 4px',padding:'8px 10px',background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:7,fontSize:11,color:'#991B1B' }}>{err}</div>
        )}

        {/* Actions */}
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:18 }}>
          <div>
            {!isNew && (
              <button onClick={archive} disabled={saving}
                style={{ background:'none',border:'1px solid #FECACA',borderRadius:7,padding:'9px 14px',
                  fontSize:12,color:'#B91C1C',cursor:'pointer',fontFamily:'inherit' }}>Archive</button>
            )}
          </div>
          <div style={{ display:'flex',gap:10 }}>
            <button onClick={() => !saving && onClose()} disabled={saving}
              style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 16px',fontSize:12,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>Cancel</button>
            <button onClick={save} disabled={saving || !name.trim() || !vendorId}
              style={{ background:(!name.trim()||!vendorId) ? '#E2E8F0' : G,border:'none',borderRadius:7,padding:'9px 20px',
                fontSize:12,color:'#fff',cursor:(saving||!name.trim()||!vendorId)?'not-allowed':'pointer',fontFamily:'inherit',fontWeight:500 }}>
              {saving ? 'Saving…' : (isNew ? 'Create matrix' : 'Save matrix')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Live "pick colors + qty → price" preview, echoing the distributor's lookup.
function LookupPreview({ type, pricing }) {
  const [qty, setQty] = useState(200);
  const [rowKey, setRowKey] = useState(type === 'screen_print' ? 3 : 0);
  // Heat transfer only: an actual sq-in number to test, mirroring the real
  // job editor's width × height inputs — a band alone isn't enough to
  // preview the real per-piece cost, since the matrix is priced PER SQUARE
  // INCH, not a flat rate per band (see the multiplication below).
  const [sqIn, setSqIn] = useState(9);

  // If rows are removed (e.g. deleting size/stitch bands) after rowKey was
  // set, the previously-selected index can point past the end of the
  // (now shorter) rows array. The <select> below still displays the first
  // remaining option's label in that case — silently misleading — while the
  // lookup below it keeps using the stale, now-invalid index and finds
  // nothing. Clamp back to a valid index whenever the rows shrink.
  useEffect(() => {
    if (type === 'screen_print' || type === 'heat_transfer') return; // heat_transfer resolves its row from sqIn, not a stored index
    if (rowKey >= pricing.rows.length) setRowKey(0);
  }, [pricing.rows.length, rowKey, type]);

  const tiers = pricing.qty_tiers || [];
  const ti = resolveTierIndex(tiers, Number(qty));
  let rowIdx = 0;
  if (type === 'screen_print') {
    rowIdx = pricing.rows.findIndex(r => Number(r.colors) === Number(rowKey));
  } else if (type === 'heat_transfer') {
    rowIdx = pricing.rows.findIndex(r => {
      const min = Number(r.min) || 0;
      const max = (r.max == null || r.max === '') ? Infinity : Number(r.max);
      return Number(sqIn) >= min && Number(sqIn) <= max;
    });
  } else if (type === 'embroidery') {
    rowIdx = Number(rowKey);
  }
  const rawPrice = (ti >= 0 && rowIdx >= 0 && pricing.rows[rowIdx]) ? pricing.rows[rowIdx].prices[ti] : null;
  // The matrix cell is $/sq-in for heat transfer (matches the actual pricing
  // sheets this was modeled on) — screen print/embroidery cells are already
  // the final per-piece rate, no multiplication needed.
  const price = (rawPrice != null && type === 'heat_transfer') ? Number(rawPrice) * Number(sqIn) : rawPrice;

  return (
    <div style={{ background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:10,padding:14 }}>
      <div style={{ fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.4px',color:'#64748B',marginBottom:10 }}>
        Price check
      </div>
      <div style={{ display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',fontSize:13,color:'#0F172A' }}>
        {type === 'screen_print' && (
          <label style={{ display:'flex',alignItems:'center',gap:6 }}>Colors
            <select value={rowKey} onChange={e => setRowKey(Number(e.target.value))} style={{ ...inputStyle,width:64 }}>
              {pricing.rows.map(r => <option key={r.colors} value={r.colors}>{r.colors}</option>)}
            </select>
          </label>
        )}
        {type === 'heat_transfer' && (
          <label style={{ display:'flex',alignItems:'center',gap:6 }}>Sq in
            <input value={sqIn} onChange={e => setSqIn(e.target.value.replace(/[^0-9.]/g,''))} style={{ ...inputStyle,width:70 }}/>
            <span style={{ color:'#94A3B8',fontSize:12 }}>
              {rowIdx >= 0 && pricing.rows[rowIdx] ? `band ${pricing.rows[rowIdx].min}–${pricing.rows[rowIdx].max == null ? '∞' : pricing.rows[rowIdx].max}` : 'no matching band'}
            </span>
          </label>
        )}
        {type === 'embroidery' && (
          <label style={{ display:'flex',alignItems:'center',gap:6 }}>Band
            <select value={rowKey} onChange={e => setRowKey(Number(e.target.value))} style={{ ...inputStyle,width:130 }}>
              {pricing.rows.map((r, i) => <option key={i} value={i}>{r.min}–{r.max == null ? '∞' : r.max}</option>)}
            </select>
          </label>
        )}
        <label style={{ display:'flex',alignItems:'center',gap:6 }}>Qty
          <input value={qty} onChange={e => setQty(e.target.value)} style={{ ...inputStyle,width:84 }}/>
        </label>
        <span style={{ color:'#94A3B8' }}>
          {ti >= 0 ? `tier ${tierLabel(tiers[ti])}` : 'no matching tier'}
        </span>
        <div style={{ marginLeft:'auto',fontSize:18,fontWeight:700,color: price==null ? '#B91C1C' : G }}>
          {price == null ? 'Unavailable' : `$${Number(price).toFixed(2)}/pc`}
        </div>
      </div>
    </div>
  );
}

// Smooth numeric input. The old cell coerced to Number on every keystroke, so
// typing "1." or "0.05" fought the user (the dot/zeros vanished). This keeps a
// local draft string while focused and only emits a parsed number (or null),
// normalizing on blur.
function NumInput({ value, onChange, integer = false, placeholder = '', style }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setDraft(value == null ? '' : String(value)); }, [value, focused]);
  const ok = (raw) => integer ? /^[0-9]*$/.test(raw) : /^[0-9]*\.?[0-9]*$/.test(raw);
  const handle = (raw) => {
    if (raw !== '' && !ok(raw)) return; // ignore invalid keystrokes
    setDraft(raw);
    if (raw === '' || raw === '.') { onChange(null); return; }
    const n = Number(raw);
    onChange(Number.isFinite(n) ? n : null);
  };
  const blur = () => {
    setFocused(false);
    if (draft === '' || draft === '.') { setDraft(''); return; }
    const n = Number(draft);
    setDraft(Number.isFinite(n) ? String(integer ? Math.round(n) : n) : '');
  };
  return (
    <input value={draft} onFocus={() => setFocused(true)} onBlur={blur}
      onChange={e => handle(e.target.value)} placeholder={placeholder}
      inputMode={integer ? 'numeric' : 'decimal'} style={style || cellStyle} />
  );
}

function PriceCell({ value, onChange }) {
  return <NumInput value={value} onChange={onChange} placeholder="—" style={cellStyle} />;
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize:12,fontWeight:700,color:'#0F172A',marginBottom:4 }}>{children}</div>
  );
}

const thStyle = { textAlign:'left',padding:'8px 10px',color:'#64748B',fontWeight:700,
  textTransform:'uppercase',letterSpacing:'0.4px',fontSize:10,whiteSpace:'nowrap' };
const tdStyle = { padding:'4px 6px' };
const ghostBtn = { border:'1px dashed #CBD5E1',background:'none',borderRadius:8,padding:'6px 12px',
  fontSize:12,color:G,fontWeight:500,cursor:'pointer',fontFamily:'inherit' };

// ─── Vendor create/edit modal ────────────────────────────────────────────────
function VendorModal({ vendor, getToken, showToast, onClose, onSaved }) {
  const isNew = !vendor;
  const [name, setName]   = useState(vendor?.name || '');
  const [email, setEmail] = useState(vendor?.email || '');
  const [phone, setPhone] = useState(vendor?.phone || '');
  const [notes, setNotes] = useState(vendor?.notes || '');
  const a = vendor?.address || {};
  const [addr, setAddr] = useState({
    line1: a.line1 || '', line2: a.line2 || '', city: a.city || '',
    state: a.state || '', postal_code: a.postal_code || '', country: a.country || 'US',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const upd = (k, v) => setAddr(s => ({ ...s, [k]: v }));

  const save = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr(null);
    try {
      const token = await getToken();
      const t = api.withToken(token);
      const body = {
        name: name.trim(), email: email.trim() || null, phone: phone.trim() || null,
        notes: notes.trim() || null, address: addr,
      };
      const saved = isNew ? await t.createVendor(body) : await t.updateVendor(vendor.id, body);
      showToast?.(isNew ? 'Decorator added' : 'Decorator saved', 'success');
      onSaved(saved);
    } catch (e) {
      setErr(e.message || 'Could not save');
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm('Archive this decorator? Matrices assigned to it will show as unassigned.')) return;
    setSaving(true);
    try {
      const token = await getToken();
      await api.withToken(token).archiveVendor(vendor.id);
      showToast?.('Decorator archived', 'success');
      onSaved(null);
    } catch (e) {
      setErr(e.message || 'Could not archive');
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',zIndex:210,
      display:'flex',alignItems:'flex-start',justifyContent:'center',backdropFilter:'blur(4px)',
      padding:'5vh 24px',overflowY:'auto' }}
      onClick={e => e.target === e.currentTarget && !saving && onClose()}>
      <div style={{ background:'#fff',borderRadius:14,padding:26,maxWidth:520,width:'100%',
        boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize:18,fontWeight:600,color:'#0F172A',marginBottom:16 }}>
          {isNew ? 'Add a decorator' : 'Edit decorator'}
        </div>

        <div style={{ marginBottom:10 }}>
          <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} autoFocus
            placeholder="e.g. Print Ready Art" style={inputStyle}/>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10 }}>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email"
              placeholder="orders@decorator.com" style={inputStyle}/>
          </div>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Phone</label>
            <input value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="206-555-0100" style={inputStyle}/>
          </div>
        </div>

        <div style={{ background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:8,padding:12,marginBottom:12 }}>
          <div style={{ fontSize:11,fontWeight:600,color:'#0F172A',marginBottom:8 }}>
            Ship-to address <span style={{ color:'#94A3B8',fontWeight:400 }}>(where blanks get sent)</span>
          </div>
          <div style={{ marginBottom:8 }}>
            <input value={addr.line1} onChange={e => upd('line1', e.target.value)} placeholder="Street" style={inputStyle}/>
          </div>
          <div style={{ marginBottom:8 }}>
            <input value={addr.line2} onChange={e => upd('line2', e.target.value)} placeholder="Suite / unit (optional)" style={inputStyle}/>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:8 }}>
            <input value={addr.city} onChange={e => upd('city', e.target.value)} placeholder="City" style={inputStyle}/>
            <input value={addr.state} onChange={e => upd('state', e.target.value)} placeholder="State" style={inputStyle}/>
            <input value={addr.postal_code} onChange={e => upd('postal_code', e.target.value)} placeholder="ZIP" style={inputStyle}/>
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="Turnaround, contact person, anything to remember…"
            style={{ ...inputStyle, resize:'vertical', minHeight:48 }}/>
        </div>

        {err && (
          <div style={{ marginBottom:12,padding:'8px 10px',background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:7,fontSize:11,color:'#991B1B' }}>{err}</div>
        )}
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between' }}>
          <div>
            {!isNew && (
              <button onClick={archive} disabled={saving}
                style={{ background:'none',border:'1px solid #FECACA',borderRadius:7,padding:'9px 14px',
                  fontSize:12,color:'#B91C1C',cursor:'pointer',fontFamily:'inherit' }}>Archive</button>
            )}
          </div>
          <div style={{ display:'flex',gap:10 }}>
            <button onClick={onClose} disabled={saving}
              style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 16px',fontSize:12,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>Cancel</button>
            <button onClick={save} disabled={saving || !name.trim()}
              style={{ background:!name.trim() ? '#E2E8F0' : G,border:'none',borderRadius:7,padding:'9px 18px',fontSize:12,color:'#fff',cursor:(saving||!name.trim())?'not-allowed':'pointer',fontFamily:'inherit',fontWeight:500 }}>
              {saving ? 'Saving…' : (isNew ? 'Add decorator' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
